import debug from "debug";
import { Observable, of, Subject } from "rxjs";
import { map, shareReplay, takeUntil } from "rxjs/operators";
import { ExtendedMediaStream } from "./ExtendedMediaStream";

export class PlainExtendedMediaStream
  extends EventTarget
  implements ExtendedMediaStream
{
  private readonly _audioTracks$: Observable<MediaStreamTrack[]>;
  private readonly _videoTracks$: Observable<MediaStreamTrack[]>;
  private readonly _addtrack$: Observable<MediaStreamTrack>;
  private readonly _removetrack$: Observable<MediaStreamTrack>;
  protected readonly _destroy$ = new Subject<void>();
  private readonly _debug: debug.Debugger;

  constructor(private readonly _stream: MediaStream) {
    super();

    this._audioTracks$ = new Observable<MediaStreamTrack[]>((_) => {
      const handler = () => {
        _.next(this._stream.getAudioTracks());
      };

      this.addEventListener("addtrack", handler);
      this.addEventListener("removetrack", handler);
      this.addEventListener("flush", handler);

      handler();

      return () => {
        this.removeEventListener("addtrack", handler);
        this.removeEventListener("removetrack", handler);
        this.removeEventListener("flush", handler);
      };
    }).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this._destroy$)
    );

    this._videoTracks$ = new Observable<MediaStreamTrack[]>((_) => {
      const handler = () => {
        _.next(this._stream.getVideoTracks());
      };

      this.addEventListener("addtrack", handler);
      this.addEventListener("removetrack", handler);
      this.addEventListener("flush", handler);

      handler();

      return () => {
        this.removeEventListener("addtrack", handler);
        this.removeEventListener("removetrack", handler);
        this.removeEventListener("flush", handler);
      };
    }).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this._destroy$)
    );

    this._addtrack$ = new Observable<MediaStreamTrack>((_) => {
      const handler = (event: Event) => {
        _.next((event as MediaStreamTrackEvent).track);
      };

      this.addEventListener("addtrack", handler);

      return () => {
        this.removeEventListener("addtrack", handler);
      };
    }).pipe(takeUntil(this._destroy$));

    this._removetrack$ = new Observable<MediaStreamTrack>((_) => {
      const handler = (event: Event) =>
        _.next((event as MediaStreamTrackEvent).track);

      this.addEventListener("removetrack", handler);

      return () => {
        this.removeEventListener("removetrack", handler);
      };
    }).pipe(takeUntil(this._destroy$));

    this._debug = debug("youm-lib:PlainExtendedMediaStream");
  }

  get stream() {
    return this._stream;
  }

  get tracks() {
    return this._stream.getTracks();
  }

  get videoTracks() {
    return this._stream.getVideoTracks();
  }

  get audioTracks() {
    return this._stream.getAudioTracks();
  }

  get videoTracks$() {
    return this._videoTracks$;
  }

  get audioTracks$() {
    return this._audioTracks$;
  }

  get addTrack$() {
    return this._addtrack$;
  }

  get removeTrack$() {
    return this._removetrack$;
  }

  get videoDevicesIds() {
    return this._stream
      .getVideoTracks()
      .map((_) => _.getSettings().deviceId)
      .filter((_): _ is string => !!_);
  }

  get audioDevicesIds() {
    return this._stream
      .getAudioTracks()
      .map((_) => _.getSettings().deviceId)
      .filter((_): _ is string => !!_);
  }

  get loaded$() {
    return of(void 0);
  }

  get isAudioEnabled() {
    return this.audioTracks.every((_) => _.enabled);
  }

  get isAudioEnabled$() {
    return this.audioTracks$.pipe(map((_) => _.every((_) => _.enabled)));
  }

  get isVideoEnabled() {
    return this.videoTracks.every((_) => _.enabled);
  }

  get isVideoEnabled$() {
    return this.videoTracks$.pipe(map((_) => _.every((_) => _.enabled)));
  }

  destroy() {
    const debug = this._debug.extend("destroy");

    debug("run destroy");

    this._stream.getTracks().forEach((_) => {
      this._stream.removeTrack(_);
      _.stop();
    });

    this._destroy$.next();
    this._destroy$.complete();
  }

  addTrack(track: MediaStreamTrack) {
    const onEnded = () => {
      this.removeTrack2(track);
      track.removeEventListener("ended", onEnded);
    };

    track.addEventListener("ended", onEnded);

    this._stream.addTrack(track);
    this.dispatchEvent(new MediaStreamTrackEvent("addtrack", { track }));
  }

  removeTrack(track: MediaStreamTrack) {
    this.removeTrack2(track);
  }

  clone() {
    return new PlainExtendedMediaStream(this._stream.clone());
  }

  audioEnable() {
    this.audioTracks.forEach((_) => (_.enabled = true));
    this.flush();
  }

  audioDisable() {
    this.audioTracks.forEach((_) => (_.enabled = false));
    this.flush();
  }

  videoEnable() {
    this.videoTracks.forEach((_) => (_.enabled = true));
    this.flush();
  }

  videoDisable() {
    this.videoTracks.forEach((_) => (_.enabled = false));
    this.flush();
  }

  // Flush non-reactive changes on the MediaStream,
  // to have reactive updates.
  private flush() {
    this.dispatchEvent(new Event("flush"));
  }

  private removeTrack2(track: MediaStreamTrack) {
    this._stream.removeTrack(track);
    this.dispatchEvent(new MediaStreamTrackEvent("removetrack", { track }));
  }
}
